import { ExclamationCircleFilled } from "@ant-design/icons";

import type { ComparisonStatus, WeatherImpactRow } from "../domain/types";
import { EstimateQuality } from "./EstimateQuality";

interface WeatherListProps {
  rows: WeatherImpactRow[];
  selectedRowId?: string | null;
  onRowSelect?: (row: WeatherImpactRow) => void;
}

const comparisonLabel: Record<ComparisonStatus, string> = {
  gap: "警告",
  aligned: "相符",
  insufficient: "提示",
};

const removedGenericWarnings = new Set([
  "当前信息不支持可靠比较",
  "数据时效、字段或置信度不足，无法可靠比较",
]);

const rainFormatter = new Intl.NumberFormat("zh-CN", {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

const rainCategories = ["无明显降雨", "小雨", "中雨", "大雨", "暴雨", "大暴雨", "特大暴雨"] as const;

function formatRainMm(value: number) {
  return rainFormatter.format(value);
}

function formatNumberRange(range: [number, number], suffix: string) {
  const [lower, upper] = range;
  return lower === upper
    ? `${formatRainMm(lower)}${suffix}`
    : `${formatRainMm(lower)}–${formatRainMm(upper)}${suffix}`;
}

function forecastRainSeverity(weatherText: string) {
  let severity = 0;
  rainCategories.forEach((category, index) => {
    if (weatherText.includes(category)) severity = Math.max(severity, index);
  });
  return severity;
}

function estimatedRainCategory(amountMm: number) {
  if (amountMm < 0.1) return rainCategories[0];
  if (amountMm < 10) return rainCategories[1];
  if (amountMm < 25) return rainCategories[2];
  if (amountMm < 50) return rainCategories[3];
  return rainCategories[4];
}

function combinedForecast(row: WeatherImpactRow) {
  const rainRange: [number, number] = row.impactRainMm
    ? [Math.max(row.rainMm, row.impactRainMm[0]), Math.max(row.rainMm, row.impactRainMm[1])]
    : [row.rainMm, row.rainMm];
  const windRange: [number, number] = row.impactWindScale
    ? [Math.max(row.windScale, row.impactWindScale[0]), Math.max(row.windScale, row.impactWindScale[1])]
    : [row.windScale, row.windScale];
  const estimatedCategory = estimatedRainCategory((rainRange[0] + rainRange[1]) / 2);
  const weatherText = forecastRainSeverity(estimatedCategory) > forecastRainSeverity(row.weatherText)
    ? estimatedCategory
    : row.weatherText;

  return {
    headline: `${weatherText} · ${formatNumberRange(windRange, "级")}`,
    rain: formatNumberRange(rainRange, "mm"),
  };
}

function formatTimeRange(startTime: string, endTime: string) {
  const formatter = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${formatter.format(new Date(startTime))} – ${formatter.format(new Date(endTime))}`;
}

export function WeatherList({ rows, selectedRowId = null, onRowSelect }: WeatherListProps) {
  return (
    <section aria-label="未来 72 小时逐时天气" className="weather-list">
      <div className="weather-list__columns" aria-hidden>
        <span className="weather-row__alert-slot" />
        <span>时间</span>
        <span>天气预报</span>
        <span>台风单因子估算</span>
        <span>综合预测</span>
      </div>
      <div className="weather-list__rows">
        {rows.slice(0, 72).map((row) => {
          const comparisonReason = removedGenericWarnings.has(row.comparison.reason)
            ? null
            : row.comparison.reason;
          const combined = combinedForecast(row);
          return (
            <article
              aria-label={comparisonReason ? `${comparisonLabel[row.comparison.status]}：${comparisonReason}` : comparisonLabel[row.comparison.status]}
              className={`weather-row${selectedRowId === row.id ? " weather-row--selected" : ""}`}
              data-comparison={row.comparison.status}
              data-testid="weather-row"
              key={row.id}
              onClick={() => onRowSelect?.(row)}
            >
              <button className="weather-row__button" type="button">
                <span
                  className="weather-row__alert-slot"
                  title={row.comparison.status === "gap" ? row.comparison.reason : undefined}
                >
                  {row.comparison.status === "gap" ? (
                    <ExclamationCircleFilled aria-hidden className="weather-row__alert-icon" />
                  ) : null}
                </span>
                <span className="weather-row__time">{formatTimeRange(row.startTime, row.endTime)}</span>
                <span className="weather-row__forecast">
                  <strong>{row.weatherText}</strong>
                  <span>{formatRainMm(row.rainMm)}mm · {row.windDirection} {row.windScale}级</span>
                </span>
                <span className="weather-row__impact">
                  <strong>
                    <EstimateQuality level="low" subject="降雨" />
                    {row.impactRainMm ? `${formatRainMm(row.impactRainMm[0])}–${formatRainMm(row.impactRainMm[1])}mm` : "—"}
                  </strong>
                  <span className="weather-row__impact-value">
                    <EstimateQuality level={row.confidence} subject="风力" />
                    {row.impactWindScale ? `${row.impactWindScale[0]}–${row.impactWindScale[1]}级` : "—"}
                  </span>
                </span>
                <span className="weather-row__combined">
                  <strong>{combined.headline}</strong>
                  <span>{combined.rain}</span>
                </span>
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}
